import { Card } from "common/Card";
import { BenefitLayout } from "providers/benefit-layout";
import { Checkbox } from "shared/components/forms/Checkbox";
import { useRentReportingIssuesVerifyRent } from "./controller";
import { format, parseISO } from "date-fns";
import { Button, ButtonVariant } from "shared/components/Button";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";
import { SuccessView } from "./slices/SuccessView";
import { useMemo, useState } from "react";
import { View } from "./types";
import { FileUploadSlice } from "./slices/FileUpload";
import { getErrorMessage } from "helpers/getErrorMessage";
import { usePostHog } from "posthog-js/react";
import { Hide } from "shared/components/helpers/Hide";
import { Modal } from "shared/components/Modal";
import { IconButton, IconButtonSize } from "shared/components/IconButton";
import { Show } from "shared/components/helpers/Show";
import { Megaphone, X } from "lucide-react";
import { useAuth } from "providers/auth";

export const RentReportingIssuesVerifyRentView = () => {
  const {
    verifications,
    bankTransactionsMeta,
    currentRv,
    activeRVIdx,
    setActiveRVIdx,
    selectedTransactionIds,
    setSelectedTransactionIds,
    suggestBankTransactions,
    submittedMonths,
    setSubmittedMonths
  } = useRentReportingIssuesVerifyRent();

  const [cashUnsupportedModal, setCashUnsupportedModal] = useState(false);
  const { partner } = useAuth();

  const theme = useMemo(() => {
    const ret: Record<string, string> = {};
    if (partner?.colors?.primary && Object.keys(partner.colors.primary).length)
      Object.entries(partner.colors.primary).forEach(([key, value]) => {
        ret[`--primary-${key}`] = value;
      });

    if (partner?.base_border_radius)
      ret["--base-border-radius"] = partner.base_border_radius;

    if (partner?.colors?.background)
      Object.entries(partner.colors.background).forEach(([key, value]) => {
        ret[`--background-${key}`] = value;
      });

    if (partner?.font_family) {
      if (partner.font_family_link) {
        const link = document.createElement("link");
        link.rel = "stylesheet";
        link.href = partner?.font_family_link;
        if (partner.font_family_link.includes("fonts.googleapis.com")) {
          const preconnect = document.createElement("link");
          preconnect.rel = "preconnect";
          preconnect.href = "https://fonts.gstatic.com";
          document.head.appendChild(preconnect);
          const preconnect2 = document.createElement("link");
          preconnect2.rel = "preconnect";
          preconnect2.href = "https://fonts.googleapis.com";
          preconnect2.crossOrigin = "anonymous";
          document.head.appendChild(preconnect2);
        }
        document.head.appendChild(link);
      }

      ret["--font-family"] = partner.font_family;
    }
    return ret;
  }, [partner]);

  const { featureFlags } = usePostHog();

  // For Chime. The shouldn't have access to settings page. Not sure if this
  // relevant to all partners
  const isAddAccountHidden = Boolean(
    featureFlags?.isFeatureEnabled("hide_hub_page")
  );

  // For Chime. But In general for everyone else, but modal has Chime specific
  // content. So we need to show the modal for Chime users only
  const cashUnsupported = Boolean(
    featureFlags?.isFeatureEnabled("cash_unsupported")
  );

  // For Chime. Should not have upload docs option hide-pvi-docs-upload
  const isUploadDocsHidden = Boolean(
    featureFlags?.isFeatureEnabled("hide-pvi-docs-upload")
  );

  const [view, setView] = useState<View>(View.SelectTransactionView);

  if (view === View.SuccessView)
    return <SuccessView monthsReviewed={submittedMonths} />;

  if (!currentRv) return null;

  const onFinish = ({ submitMonth }: { submitMonth: boolean }) => {
    setSelectedTransactionIds([]);
    if (submitMonth)
      setSubmittedMonths([...submittedMonths, currentRv.for_month]);

    if ((activeRVIdx || 0) + 1 === verifications?.length)
      setView(View.SuccessView);
    else setActiveRVIdx((activeRVIdx || 0) + 1);
  };

  if (view === View.UploadFileView)
    return (
      <FileUploadSlice
        setView={setView}
        handleIssueUpload={({
          url,
          filename,
          description
        }: {
          url: string;
          filename: string;
          description: string;
        }) => {
          return suggestBankTransactions
            .mutateAsync({
              transaction_ids: selectedTransactionIds,
              document: {
                url,
                filename,
                description
              }
            })
            .then(() => {
              onFinish({
                submitMonth: true
              });
            })
            .catch((err) => {
              // show error toast
              toast.error(getErrorMessage(err), { autoClose: 5000 });
            });
        }}
      />
    );

  const total =
    bankTransactionsMeta.data?.data.bank_transactions.reduce(
      (acc: number, payment) => {
        if (selectedTransactionIds.includes(payment.id))
          return acc + payment.amount.cents;

        return acc;
      },
      0
    ) || 0;

  return (
    <BenefitLayout
      hasBack={true}
      title="Verify rent payment"
      mainClassName="max-w-[440px] w-full mx-auto md:mt-4"
      footer={
        <div className="max-w-[440px] mx-auto w-full">
          {(total || 0) > 0 ? (
            <div className="animate-appear">
              <h1 className="text-base font-bold text-content-1000 mb-2">
                Total:{" "}
                {Intl.NumberFormat("en-US", {
                  style: "currency",
                  currency: "USD",
                  maximumFractionDigits: 2
                }).format(total / 100)}
              </h1>
              <p className="mb-4 font-medium text-xs text-content-400">
                This is your rent payment verification for{" "}
                {format(parseISO(currentRv?.for_month), "LLLL yyyy")}
              </p>
            </div>
          ) : null}
          <Button
            disabled={!selectedTransactionIds.length}
            variant={ButtonVariant.PRIMARY}
            className="w-full"
            onClick={() => {
              suggestBankTransactions
                .mutateAsync({
                  transaction_ids: selectedTransactionIds
                })
                .then(() => {
                  onFinish({
                    submitMonth: true
                  });
                })
                .catch((err) => {
                  // show error toast
                  toast.error(getErrorMessage(err.message), {
                    autoClose: 5000
                  });
                });
            }}
          >
            Verify
          </Button>
          <Link to={"/hub"} className="w-full md:block hidden mt-2">
            <Button className="w-full">Back</Button>
          </Link>
        </div>
      }
    >
      <div className="mb-6 flex items-center justify-between">
        <p className="text-xs font-medium text-content-500">
          {(activeRVIdx || 0) + 1} of {verifications?.length} months to verify
        </p>
        {/* Progress bar */}
        <div className="h-1 bg-alpha-40 rounded-md max-w-[104px] w-full">
          <div
            className="h-full bg-primary-500 rounded-md"
            style={{
              width: `${
                (((activeRVIdx || 0) + 1) / (verifications?.length || 1)) * 100
              }%`
            }}
          />
        </div>
      </div>

      <Show if={cashUnsupported}>
        <Card className="p-4 mb-6">
          <Megaphone className="text-content-1000 w-6 h-6 mb-4" />

          <p className="text-sm text-content-1000 font-medium">
            We currently can&apos;t report rent payments made in cash.{" "}
            <span
              className="underline"
              onClick={() => {
                setCashUnsupportedModal(true);
              }}
            >
              See supported payment types
            </span>
          </p>
        </Card>
      </Show>

      <Card className="mb-6">
        <h2 className="p-4 border-b border-alpha-40 text-center text-content-1000 text-base font-bold">
          {format(parseISO(currentRv?.for_month), "LLLL yyyy")}
        </h2>
        {bankTransactionsMeta.data?.data.bank_transactions.length ? (
          bankTransactionsMeta.data?.data.bank_transactions.map((payment) => (
            <Checkbox
              key={payment.id}
              wrapperClassName="last-of-type:border-none border-b border-alpha-40 p-4"
              onChange={(e) => {
                if (e.target.checked)
                  setSelectedTransactionIds([
                    ...selectedTransactionIds,
                    payment.id
                  ]);
                else
                  setSelectedTransactionIds(
                    selectedTransactionIds.filter((id) => id !== payment.id)
                  );
              }}
              checked={selectedTransactionIds.includes(payment.id)}
              labelClassName="flex items-center w-full"
              label={
                <>
                  <div>
                    <p className="text-content-700 text-sm font-medium max-w-[250px] truncate mb-1">
                      {payment.name}
                    </p>
                    <p className="text-content-300 text-xs">
                      {format(parseISO(payment.date), "LLL d")}
                    </p>
                  </div>
                  <p className="text-content-500 ml-auto">
                    {Intl.NumberFormat("en-US", {
                      style: "currency",
                      currency: payment.amount.currency || "USD",
                      maximumFractionDigits: 2
                    }).format(payment.amount.cents / 100)}
                  </p>
                </>
              }
            />
          ))
        ) : bankTransactionsMeta.isLoading ? (
          <p className="p-4 text-content-300 text-center">Loading...</p>
        ) : (
          <p className="p-4 text-content-300 text-center">
            No transactions found for this month.
            {!isAddAccountHidden || !isUploadDocsHidden ? (
              <>
                {" "}
                {!isAddAccountHidden && !isUploadDocsHidden
                  ? "Please try connecting a new bank account or upload a receipt."
                  : !isAddAccountHidden
                    ? "Please try connecting a new bank account."
                    : "Please try to upload a receipt."}
              </>
            ) : null}
          </p>
        )}
      </Card>

      <Hide
        if={
          isAddAccountHidden &&
          isUploadDocsHidden &&
          (activeRVIdx || 0) + 1 === verifications?.length
        }
      >
        <Card className="p-6">
          {!isAddAccountHidden || !isUploadDocsHidden ? (
            <>
              <h1 className="text-base font-bold text-content-1000 mb-2.5">
                Don&apos;t see your rent payment?
              </h1>

              <p className="text-xs font-medium text-content-300 mb-6">
                {!isAddAccountHidden && !isUploadDocsHidden
                  ? "If you paid a different way this month, please link a new bank account to be scanned, or upload a receipt (e.g. money order receipt)."
                  : !isAddAccountHidden
                    ? "If you paid a different way this month, please link a new bank account to be scanned."
                    : "If you paid a different way this month, please upload a receipt (e.g. money order receipt)."}
              </p>
            </>
          ) : null}

          <Hide if={isAddAccountHidden}>
            <Link to="/settings/linked-accounts">
              <Button className="w-full mb-2">Add account</Button>
            </Link>
          </Hide>

          <Hide if={isUploadDocsHidden}>
            <Button
              className="w-full"
              onClick={() => {
                setView(View.UploadFileView);
              }}
            >
              Add documents
            </Button>
          </Hide>

          {(activeRVIdx || 0) + 1 !== verifications?.length && (
            <Button
              borderLess
              className="w-full"
              onClick={() =>
                onFinish({
                  submitMonth: false
                })
              }
            >
              Skip for now
            </Button>
          )}
        </Card>
      </Hide>

      {cashUnsupportedModal ? (
        <Modal
          opened={cashUnsupportedModal}
          panelClassName="!bottom-0  max-w-[460px] w-full !rounded-t-4 rounded-b-none !h-auto p-4 animate-appear absolute"
          onClose={() => setCashUnsupportedModal(false)}
        >
          <div style={theme}>
            <IconButton
              Icon={X}
              borderLess
              size={IconButtonSize.SM}
              className="-translate-x-2 -translate-y-2"
              onClick={() => setCashUnsupportedModal(false)}
            />

            <h1 className="text-xl font-bold text-content-1000 mb-2">
              Supported payments
            </h1>

            <p className="mb-4 text-sm text-content-600">
              Choose your preferred way to pay rent on time with Chime - Pay
              Anyone, ACH transfer, Credit Builder, Debit card, or Mail a check
            </p>

            <Button
              className="w-full mt-4 mb-4"
              variant={ButtonVariant.PRIMARY}
              onClick={() => {
                setCashUnsupportedModal(false);
              }}
            >
              Got it
            </Button>
          </div>
        </Modal>
      ) : null}
    </BenefitLayout>
  );
};
